import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Gift, CheckCircle2, Send, Users, Sparkles, ShieldCheck } from "lucide-react";

const initialForm = {
  referrer_name: "", referrer_email: "", referrer_phone: "",
  referee_name: "", referee_email: "", referee_phone: "", referee_company: "",
  project_type: "gruendung", note: "", accepts_terms: false,
};

export default function EmpfehlungsPage() {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/referrals", form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Es ist ein Fehler aufgetreten.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="empfehlen-done">
        <div className="mbw-container max-w-2xl text-center">
          <div className="bg-white rounded-2xl p-12 border border-slate-200">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2433] mb-4">Vielen Dank!</h1>
            <p className="text-slate-600 leading-relaxed mb-8">
              Ihre Empfehlung ist bei uns eingegangen. Wir nehmen innerhalb von zwei Werktagen Kontakt mit
              der empfohlenen Person auf. Sobald ein Gründungsvertrag unterschrieben ist, schreiben wir Ihnen
              <strong> CHF 500 Gutschrift</strong> auf das nächste Honorar gut.
            </p>
            <Link to="/" className="btn-primary" data-testid="empfehlen-back-home">Zurück zur Startseite</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="empfehlen-page">
      <div className="mbw-container max-w-5xl">

        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="mbw-overline mb-4">Empfehlungsprogramm</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
            Empfehlen Sie uns weiter – <span className="text-[#991B1B]">und profitieren Sie beide.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Wer uns kennt, weiss: Wir liefern saubere Arbeit, klare Kommunikation und einen festen
            Ansprechpartner. Wenn Sie jemanden kennen, der eine Schweizer Gesellschaft gründen möchte,
            verdoppelt sich der Mehrwert: für den neuen Mandanten <strong>und</strong> für Sie.
          </p>
        </div>

        {/* How it works */}
        <div className="grid md:grid-cols-3 gap-5 mb-14" data-testid="empfehlen-benefits">
          {[
            { Icon: Users, title: "Sie empfehlen", desc: "Sie nennen uns einen Interessenten für eine Firmengründung in der Schweiz – über das Formular unten." },
            { Icon: Sparkles, title: "Der neue Mandant gewinnt", desc: "Kostenloses Erstgespräch (45 Min), individuelle Einordnung – ohne Verkaufsdruck, ohne Verpflichtung." },
            { Icon: Gift, title: "Sie erhalten Gutschrift", desc: "Sobald ein Gründungsvertrag unterschrieben ist, schreiben wir Ihnen CHF 500 auf das nächste Honorar gut." },
          ].map((b, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200" data-testid={`empfehlen-step-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-[#991B1B] flex items-center justify-center mb-4">
                <b.Icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Schritt {i + 1}</div>
              <h2 className="font-heading font-bold text-[#1A2433] text-lg mb-2">{b.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Conditions */}
        <div className="bg-[#1A2433] text-white rounded-2xl p-8 mb-12" data-testid="empfehlen-conditions">
          <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#DC2626]" /> Konditionen im Überblick</h2>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-200">
            <li>✓ Aktuell nur für <strong>Firmengründungen</strong> in der Schweiz</li>
            <li>✓ Belohnung: <strong>CHF 500 Gutschrift</strong> für Sie</li>
            <li>✓ Kostenloses Erstgespräch für den/die Empfohlene:n</li>
            <li>✓ Auszahlung als Gutschrift, sobald Vertrag unterschrieben ist</li>
            <li>✓ Max. <strong>5 erfolgreiche Empfehlungen</strong> pro Werber pro Kalenderjahr</li>
            <li>✓ Empfohlene Person darf nicht bereits Mandant:in sein</li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-white rounded-2xl p-8 md:p-10 border border-slate-200" data-testid="empfehlen-form">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-2">Empfehlung absenden</h2>
          <p className="text-sm text-slate-500 mb-8">Wir behandeln alle Angaben streng vertraulich.</p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Werber */}
            <fieldset className="space-y-3">
              <legend className="font-semibold text-[#1A2433] mb-2">Ihre Angaben (Werber:in)</legend>
              <Field label="Name *" testid="ref-werber-name"
                     value={form.referrer_name} onChange={(v) => setForm({ ...form, referrer_name: v })} required />
              <Field label="E-Mail *" type="email" testid="ref-werber-email"
                     value={form.referrer_email} onChange={(v) => setForm({ ...form, referrer_email: v })} required />
              <Field label="Telefon (optional)" testid="ref-werber-phone"
                     value={form.referrer_phone} onChange={(v) => setForm({ ...form, referrer_phone: v })} />
            </fieldset>

            {/* Referee */}
            <fieldset className="space-y-3">
              <legend className="font-semibold text-[#1A2433] mb-2">Empfohlene Person</legend>
              <Field label="Name *" testid="ref-referee-name"
                     value={form.referee_name} onChange={(v) => setForm({ ...form, referee_name: v })} required />
              <Field label="E-Mail *" type="email" testid="ref-referee-email"
                     value={form.referee_email} onChange={(v) => setForm({ ...form, referee_email: v })} required />
              <Field label="Telefon (optional)" testid="ref-referee-phone"
                     value={form.referee_phone} onChange={(v) => setForm({ ...form, referee_phone: v })} />
              <Field label="Firma / Vorhaben (optional)" testid="ref-referee-company"
                     value={form.referee_company} onChange={(v) => setForm({ ...form, referee_company: v })} />
            </fieldset>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-semibold text-[#1A2433] mb-1.5">Notiz für uns (optional)</label>
            <textarea
              className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:border-[#991B1B]"
              rows={3}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="z. B. konkretes Anliegen, bevorzugter Zeitpunkt für die Kontaktaufnahme…"
              data-testid="ref-note"
            />
          </div>

          <label className="flex items-start gap-2 mt-6 text-sm text-slate-600 cursor-pointer select-none" data-testid="ref-terms-wrapper">
            <input
              type="checkbox"
              required
              checked={form.accepts_terms}
              onChange={(e) => setForm({ ...form, accepts_terms: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#991B1B]"
              data-testid="ref-terms-checkbox"
            />
            <span>
              Ich bestätige, dass ich das Einverständnis der empfohlenen Person zur Weitergabe ihrer
              Kontaktdaten an die MBW Swiss AG eingeholt habe. Ich habe die{" "}
              <Link to="/datenschutz" className="text-[#991B1B] underline hover:no-underline">Datenschutzerklärung</Link> und die{" "}
              <Link to="/impressum#agb" className="text-[#991B1B] underline hover:no-underline">AGB</Link> gelesen.
            </span>
          </label>

          {error && <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-md" data-testid="ref-error">{error}</div>}

          <div className="mt-6 flex justify-end">
            <button type="submit" disabled={busy || !form.accepts_terms} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="ref-submit">
              {busy ? "Sende…" : <>Empfehlung absenden <Send className="w-4 h-4" /></>}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-600 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:outline-none focus:border-[#991B1B]"
        data-testid={testid}
      />
    </label>
  );
}
